import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_NAME, SITE_NAME_IS_DEFAULT, brand, NOINDEX } from '@/lib/siteConfig';
import { programText } from '@/lib/calcUnlock';
import { useCanonical, useOgUrl, useMetaByName } from '@/lib/canonical';
import MONEY_SEO_RAW from '@/seo/money.json';
import PARA_SEO_RAW from '@/seo/para.json';
import CHAKRY_SEO_RAW from '@/seo/chakry.json';
import CHAKRY_PARA_SEO_RAW from '@/seo/chakry_para.json';
import OBRAZ_SEO_RAW from '@/seo/obraz.json';
import VLYUBIT_SEO_RAW from '@/seo/vlyubit.json';
import NUMEROLOGY_SEO_RAW from '@/seo/numerology.json';
import UZEL_SEO_RAW from '@/seo/uzel.json';
import WORK_SEO_RAW from '@/seo/work.json';

// Тексты программ: название и адрес сайта и разовая цена — свои на рф и на
// ру (programText) — те же, что видны на странице.
const MONEY_SEO = programText(MONEY_SEO_RAW);
const PARA_SEO = programText(PARA_SEO_RAW);
const CHAKRY_SEO = programText(CHAKRY_SEO_RAW);
const CHAKRY_PARA_SEO = programText(CHAKRY_PARA_SEO_RAW);
const OBRAZ_SEO = programText(OBRAZ_SEO_RAW);
const VLYUBIT_SEO = programText(VLYUBIT_SEO_RAW);
const NUMEROLOGY_SEO = programText(NUMEROLOGY_SEO_RAW);
// /work и /uzel — новые «Профессия» и «Предназначение» (с 14.10.2026):
// title, description и h1 — из src/seo/*.json, тот же файл читает server.js.
const WORK_SEO = programText(WORK_SEO_RAW);
const UZEL_SEO = programText(UZEL_SEO_RAW);

// Punycode-форма origin для og:image (new URL() сам делает IDNA-кодирование).
function toAsciiOrigin(origin) {
  try { return new URL(origin).origin; } catch (_) { return origin; }
}

// Default OG image — same fix as SITE_URL below: this pointed at
// astrofeel.ru/og-default.jpg, a foreign domain with no such file (that
// site is a separate sibling product, not this one). Punycode form used
// (not "https://астрофил.рф/...") because some social-share parsers read
// cyrillic-domain og:image URLs unreliably — same reasoning as the
// PUBLIC_SITE_ORIGIN_ASCII fallback in frontend/server.js.
// og:image берётся с канонического домена (SITE_URL); для кириллического
// домена — в punycode, см. комментарий выше.
const DEFAULT_OG_IMAGE = `${toAsciiOrigin(SITE_URL)}/og-image.png`;
// Canonical site origin — set to the cyrillic primary domain so every
// canonical link emitted by this component points to https://астрофил.рф/...
// (matches the production primary mirror and the canonicals already
// registered for the 8 public pages via <CanonicalRf>). Previously this
// was 'https://astrofeel.ru', which caused the homepage and several
// public pages on астрофил.рф to declare astrofeel.ru as canonical —
// GSC then classified астрофил.рф/ as "Альтернативная страница с
// тегом canonical". One canonical domain = one source of truth.
// SITE_URL теперь приходит из src/lib/siteConfig.js (REACT_APP_SITE_URL).

// Написание бренда в SEO-текстах главной. На рф это ДВЕ формы сразу —
// «АСТРОфил (Астрофил)»: люди ищут и капсом, и обычным письмом, и обе формы
// должны быть в title и в keywords. На ру форма одна, латинская: подставлять
// туда «Астрофил» нельзя — это бренд соседнего сайта, и две площадки
// конкурировали бы за один и тот же запрос. brand() здесь не годится по той
// же причине: он размножил бы скобку в «ASTROfeel (ASTROfeel)».
const BRAND_TITLE = SITE_NAME_IS_DEFAULT ? 'АСТРОфил (Астрофил)' : SITE_NAME;
const BRAND_KEYWORDS = SITE_NAME_IS_DEFAULT ? 'АСТРОфил, Астрофил' : SITE_NAME;

// SEO data for all pages
export const SEO_DATA = {
  // Main pages
  home: {
    title: `${BRAND_TITLE} — натальная карта онлайн`,
    description: 'Построй натальную карту онлайн по дате рождения. АСТРОфил — персональная астрология: карта, транзиты, совместимость, нумерология. Бесплатно.',
    h1: 'Натальная карта по дате рождения онлайн',
    keywords: `натальная карта онлайн, натальная карта бесплатно, ${BRAND_KEYWORDS}, астрология по дате рождения`,
  },

  // ==================== SEO landing cluster (натальная карта) ====================
  // Четыре посадочные страницы под коммерческие запросы (см. App.js и
  // pages/seo/NatalSeoLandingPage.jsx). title/description/h1 здесь обязаны
  // совпадать с STATIC_SEO_BY_PATH в frontend/server.js: сервер отдаёт эти
  // теги в готовом HTML, а SEO.jsx после гидратации переписывает <title> на
  // месте — расхождение дало бы «прыжок» заголовка на одном и том же URL.
  natalChart: {
    title: 'Натальная карта онлайн с расшифровкой — построить бесплатно',
    description: 'Постройте натальную карту онлайн по дате, времени и месту рождения. Планеты в знаках и домах, аспекты и расшифровка разделов карты — бесплатно.',
    h1: 'Натальная карта онлайн',
    keywords: 'натальная карта, натальная карта онлайн, натальная карта с расшифровкой, построить натальную карту',
  },
  natalChartFree: {
    title: 'Натальная карта бесплатно онлайн — расчёт по дате рождения',
    description: 'Бесплатный расчёт натальной карты онлайн по дате рождения: колесо карты с планетами, домами и аспектами и разбор основных положений.',
    h1: 'Натальная карта бесплатно',
    keywords: 'натальная карта бесплатно, бесплатная натальная карта, рассчитать натальную карту бесплатно',
  },
  natalChartCalc: {
    title: 'Расчёт натальной карты по дате рождения онлайн',
    description: 'Расчёт натальной карты онлайн по дате, времени и месту рождения: положения планет, куспиды домов и аспекты считаются автоматически.',
    h1: 'Расчёт натальной карты',
    keywords: 'расчёт натальной карты, рассчитать натальную карту, натальная карта расчёт онлайн',
  },
  natalChartDecode: {
    title: 'Расшифровка натальной карты онлайн — что означают планеты и дома',
    description: 'Расшифровка натальной карты онлайн: что означают планеты в знаках, дома гороскопа и аспекты между планетами. Постройте карту и откройте разбор.',
    h1: 'Расшифровка натальной карты',
    keywords: 'расшифровка натальной карты, натальная карта с расшифровкой, как читать натальную карту',
  },

  // ==================== Existing pages ====================
  pricing: {
    title: 'Тарифы АСТРОфил — подписка на астрологию',
    description: 'Цены АСТРОфил: главный ответ в любой программе бесплатно, больше — после регистрации, одна программа целиком — 199 ₽, всё — в ПРО.',
    h1: 'Тарифы АСТРОфил',
  },
  consultation: {
    title: 'Личная консультация астролога — видеоразбор натальной карты | АСТРОфил',
    description: 'Личная консультация астролога Светланы Филатовой: видеоразбор Вашей натальной карты не меньше часа, который остаётся у Вас навсегда. Оставьте заявку на сайте.',
    h1: 'Личная консультация астролога',
    keywords: 'консультация астролога, личная консультация астролога, разбор натальной карты, астролог онлайн, заказать консультацию астролога',
  },
  about: {
    title: 'О нас — АСТРОфил',
    description: 'Светлана Филатова — астролог, автор системы АСТРОфил. Личный опыт многолетней практики астролога.',
    h1: 'Об авторе системы АСТРОфил',
  },
  
  sitemap: {
    title: 'Карта сайта АСТРОфил — все программы и статьи',
    description: 'Карта сайта АСТРОфил: все программы по дате рождения, хорар, натальная карта и статьи по астрологии на одной странице.',
    h1: 'Карта сайта',
  },

  // Calculator pages
  // /money: title, description и h1 приходят из src/seo/money.json — тот же
  // файл читает frontend/server.js, который отдаёт эти теги в готовом HTML.
  // Раньше здесь лежала своя копия, она разошлась с серверной, и после
  // гидрации React переписывал и title, и H1 на другие строки. Правите
  // JSON — меняются обе стороны сразу.
  money: {
    title: MONEY_SEO.title,
    description: MONEY_SEO.description,
    h1: MONEY_SEO.h1,
    keywords: 'финансовый гороскоп по дате рождения, когда будут деньги, денежный гороскоп бесплатно, деньги по натальной карте, 2 дом в натальной карте',
  },
  daily3: {
    title: 'Транзиты на сегодня по дате рождения',
    description: 'Персональный астрологический прогноз на день. Транзиты по натальной карте онлайн — бесплатно.',
    h1: 'Транзиты на сегодня по натальной карте',
    keywords: 'транзиты на сегодня, персональный прогноз на день, астрологический прогноз по дате рождения',
  },
  uzel: {
    title: UZEL_SEO.title,
    description: UZEL_SEO.description,
    h1: UZEL_SEO.h1,
    keywords: 'предназначение по дате рождения, северный узел, южный узел, лунные узлы в натальной карте',
  },
  // /para и /chakry: title, description и h1 — из src/seo/*.json, тот же
  // файл читает frontend/server.js.
  para: {
    title: PARA_SEO.title,
    description: PARA_SEO.description,
    h1: PARA_SEO.h1,
    keywords: 'совместимость по дате рождения онлайн, астрологическая совместимость бесплатно',
  },
  // /numerology: title, description и h1 — из src/seo/numerology.json, тот же
  // файл читает frontend/server.js.
  num: {
    title: NUMEROLOGY_SEO.title,
    description: NUMEROLOGY_SEO.description,
    h1: NUMEROLOGY_SEO.h1,
    keywords: 'нумерология по дате рождения, нумерологический код, рассчитать число судьбы',
  },
  work: {
    title: WORK_SEO.title,
    description: WORK_SEO.description,
    h1: WORK_SEO.h1,
    keywords: 'профессия по натальной карте, призвание по дате рождения, MC в натальной карте, 10 дом',
  },
  mum: {
    title: 'Партнёр по дате рождения — астрология',
    description: 'Анализ характера идеального партнёра по натальной карте. Астрологический разбор отношений.',
    h1: 'Партнёр по натальной карте',
    keywords: 'партнер по дате рождения, идеальный партнер астрология, отношения по натальной карте',
  },
  love: {
    title: 'Любовь и замужество: твои лучшие периоды по дате рождения',
    description: 'Колесо года для любви: самые удачные и трудные недели на 12 месяцев вперёд по натальной карте. Рассчитать онлайн.',
    h1: 'Любовь и замужество: твои лучшие периоды',
    keywords: 'лучшие периоды для любви, когда выйду замуж астрология, замужество по дате рождения, благоприятный период для брака',
  },
  chakry: {
    title: CHAKRY_SEO.title,
    description: CHAKRY_SEO.description,
    h1: CHAKRY_SEO.h1,
    keywords: 'чакры по дате рождения, чакры по натальной карте, астрология чакры, точка сборки',
  },
  chakryPara: {
    title: CHAKRY_PARA_SEO.title,
    description: CHAKRY_PARA_SEO.description,
    h1: CHAKRY_PARA_SEO.h1,
    keywords: 'совместимость по чакрам, чакры пары, чакры по дате рождения двоих, совместимость по натальной карте',
  },
  year: {
    title: 'Твой год: прогноз на год по натальной карте — удачные недели и главные события',
    description: 'Колесо года по дате рождения: удачные и трудные недели, долгие фоновые влияния, главные события года и лучший месяц для любви, денег и перемен. Рассчитать онлайн.',
    h1: 'Твой год',
    keywords: 'прогноз на год по натальной карте, гороскоп на год по дате рождения, удачные недели года, транзиты на год',
  },
  moneyPeriods: {
    title: 'Когда придут деньги: твои лучшие периоды по дате рождения',
    description: 'Колесо года для денег: самые удачные и трудные недели на 12 месяцев вперёд по натальной карте. Рассчитать онлайн.',
    h1: 'Когда придут деньги: твои лучшие периоды',
    keywords: 'когда появятся деньги астрология, денежные периоды, благоприятный период для денег, финансовый прогноз по дате рождения',
  },
  // /obraz — из src/seo/obraz.json, тот же файл читает frontend/server.js.
  obraz: {
    title: OBRAZ_SEO.title,
    description: OBRAZ_SEO.description,
    h1: OBRAZ_SEO.h1,
    keywords: 'как тебя видят, как тебя видят другие по натальной карте, асцендент и внешность, первое впечатление астрология, образ по дате рождения',
  },
  // /vlyubit — из src/seo/vlyubit.json, тот же файл читает frontend/server.js.
  vlyubit: {
    title: VLYUBIT_SEO.title,
    description: VLYUBIT_SEO.description,
    h1: VLYUBIT_SEO.h1,
    keywords: 'как его влюбить, как влюбить мужчину по дате рождения, венера в знаке мужчины, что нравится мужчине по гороскопу',
  },

  // ==================== Category landing pages (/love, /finance, /success, /forecast) ====================
  'category-love': {
    title: 'Любовь и отношения по дате рождения — АСТРОфил',
    description: 'Совместимость, партнёр и отношения по натальной карте: узнай, какие люди тебе подходят и как избежать конфликтов — бесплатно на АСТРОфил.',
    h1: 'Любовь и отношения по натальной карте',
    keywords: 'совместимость по дате рождения, партнёр по натальной карте, отношения астрология',
  },
  'category-finance': {
    title: 'Деньги и финансовая стратегия по дате рождения — АСТРОфил',
    description: 'Узнай свою денежную стратегию, источник дохода и подходящую профессию по натальной карте — бесплатно на АСТРОфил.',
    h1: 'Деньги и финансовая стратегия по натальной карте',
    keywords: 'финансовая стратегия по дате рождения, деньги астрология, профессия по натальной карте',
  },
  'category-success': {
    title: 'Реализация, таланты и предназначение — АСТРОфил',
    description: 'Найди своё дело жизни, таланты и предназначение по натальной карте и лунным узлам — бесплатно на АСТРОфил.',
    h1: 'Реализация и предназначение по натальной карте',
    keywords: 'предназначение по дате рождения, таланты по натальной карте, дело жизни астрология',
  },
  'category-forecast': {
    title: 'Прогноз по дате рождения — транзиты и хорар — АСТРОфил',
    description: 'Персональный астрологический прогноз: ресурсные и критичные дни, события месяца и ответ на конкретный вопрос — бесплатно на АСТРОфил.',
    h1: 'Астрологический прогноз по натальной карте',
    keywords: 'астрологический прогноз по дате рождения, транзиты онлайн, хорар вопрос',
  },
};

// FAQ data for schema markup
export const FAQ_DATA = {
  // SEO cluster — natal chart (minimal, structural only)
  natalChart: [
    {
      question: 'Что нужно для расчёта натальной карты?',
      answer: 'Дата, время и место рождения. Город нужен для определения часового пояса и координат.',
    },
    {
      question: 'Можно ли построить натальную карту бесплатно?',
      answer: 'Да. Базовая натальная карта рассчитывается и отображается бесплатно, без регистрации.',
    },
    {
      question: 'Нужна ли точная минута рождения?',
      answer: 'Желательно. От минуты зависит Асцендент и положение куспидов домов. Без точного времени можно построить солнечную натальную карту.',
    },
  ],
  natalChartFree: [
    {
      question: 'Что даёт бесплатная натальная карта?',
      answer: 'Колесо карты, положение планет в знаках и домах, Асцендент, ключевые аспекты и короткое описание.',
    },
    {
      question: 'Нужна ли регистрация?',
      answer: 'Нет. Расчёт доступен сразу. Аккаунт нужен, только если ты хочешь сохранить данные и получать персональные прогнозы.',
    },
  ],
  natalChartCalc: [
    {
      question: 'Как устроен расчёт натальной карты?',
      answer: 'По дате, времени и координатам места рождения вычисляется положение Солнца, Луны, планет, куспидов домов и аспекты между ними.',
    },
    {
      question: 'Что делать, если не знаешь точное время рождения?',
      answer: 'Можно построить солнечную карту — положения планет в знаках будут корректны, а дома и Асцендент — приблизительны.',
    },
  ],
  natalChartDecode: [
    {
      question: 'Как читать натальную карту?',
      answer: 'Сначала смотри ключевые точки: Солнце, Луну, Асцендент. Затем — планеты в знаках и домах, аспекты и акценты (стелиумы, ретрограды, узлы).',
    },
    {
      question: 'Чем натальная карта отличается от гороскопа?',
      answer: 'Натальная карта — твоя личная карта рождения, она не меняется. Гороскоп — общая характеристика знака зодиака без учёта времени и места.',
    },
  ],

  money: MONEY_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  daily3: [
    {
      question: 'Что такое транзиты в астрологии?',
      answer: 'Транзиты — это текущее положение планет относительно вашей натальной карты. Они показывают актуальные влияния и тенденции на конкретный день.',
    },
    {
      question: 'Как часто нужно смотреть транзиты?',
      answer: 'Рекомендуется проверять транзиты ежедневно утром для планирования дня. Особенно важно следить за транзитами Луны, которая меняет знак каждые 2-3 дня.',
    },
  ],
  // /uzel и /work — из src/seo/*.json; разметку FAQPage на этих страницах
  // отдаёт только сервер (как у /numerology), FAQSchema их не рисует.
  uzel: UZEL_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  work: WORK_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  para: PARA_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  chakry: CHAKRY_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  chakryPara: CHAKRY_PARA_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  obraz: OBRAZ_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  // /numerology — из src/seo/numerology.json. Пока вопросов там нет, разметка
  // FAQPage не выводится (FAQSchema с пустым списком ничего не рисует).
  // Прежние вопросы старой страницы (число судьбы / души / имени) к новой
  // программе не относятся и сняты вместе с ней.
  num: NUMEROLOGY_SEO.faq.map((f) => ({ question: f.q, answer: f.a })),
  mum: [
    {
      question: 'Как натальная карта показывает будущего партнёра?',
      answer: 'Портрет партнёра строится по Десценденту — точке напротив Асцендента, с которой начинается 7-й дом, дом отношений и брака: по знаку Десцендента, управителю 7-го дома и планетам в нём.',
    },
    {
      question: 'Можно ли по натальной карте узнать характер конкретного человека?',
      answer: 'Расчёт показывает собирательный портрет партнёра, к которому вас тянет по своей карте, — типаж, качества и возможные сложности в отношениях, а не описание конкретного человека.',
    },
    {
      question: 'Чем этот расчёт отличается от совместимости (синастрии)?',
      answer: 'Здесь анализируется только ваша натальная карта — какой партнёр вам близок по природе. Синастрия сравнивает две конкретные карты, вашу и партнёра.',
    },
  ],
};

export default function SEO({ 
  page = 'home',
  customTitle,
  customDescription,
  customH1,
  ogImage,
  canonicalPath,
  noindex = false,
  // false — тег robots не выводится вовсе: его уже вставил сервер
  // (frontend/server.js, NOINDEX_EXACT), второй такой же был бы дублем.
  robots = true,
  // Прежний флаг /work и /uzel; с 14.10.2026 так работают все страницы
  // (см. ниже), проп оставлен, чтобы не трогать вызовы.
  nativeMeta: _nativeMeta, // eslint-disable-line no-unused-vars
}) {
  const data = SEO_DATA[page] || SEO_DATA.home;
  
  // brand(): при REACT_APP_SITE_NAME подставляет название сайта в готовые
  // SEO-тексты; при значении по умолчанию — тождество.
  const title = brand(customTitle || data.title);
  const description = brand(customDescription || data.description);
  const keywords = brand(data.keywords);
  const image = ogImage || DEFAULT_OG_IMAGE;
  const canonical = canonicalPath ? `${SITE_URL}${canonicalPath}` : null;

  // <title> is set via the native `document.title` API instead of
  // <Helmet><title> — for routes covered by server.js's SSR head injection
  // (STATIC_SEO_BY_PATH: home, /pricing, calculators, legal pages, …) the
  // initial HTML already contains a real <title>. react-helmet-async does
  // not detect/replace that pre-existing tag on client-side mount — it
  // appends a second, Helmet-owned <title> instead, so the DOM ends up
  // with two <title> elements (the exact "found 2 title tags" audit
  // finding on `/`). `document.title = …` follows the native DOM setter
  // behaviour (WHATWG HTML "the title element in the head element"): it
  // mutates the first existing <title> in place rather than creating a
  // new one, so this stays a single node both on first load and on
  // client-side SPA navigation between routes.
  //
  // Сравнение перед записью: если сервер уже отдал ровно этот заголовок
  // (страницы из STATIC_SEO_BY_PATH), переписывать нечего — молча оставляем
  // серверный узел как есть. Так у страницы физически нет момента, когда
  // title меняется после гидрации; на страницах, где строки отличаются,
  // поведение прежнее.
  useEffect(() => {
    if (title && document.title !== title) document.title = title;
  }, [title]);

  // Canonical — тоже нативно, по той же причине, что и <title>: серверный
  // тег уже в HTML, а Helmet дописал бы второй. См. lib/canonical.js.
  useCanonical(canonical);
  // og:url — так же: серверный тег переписывается, второй не появляется.
  useOgUrl(canonical);
  // Description и robots — тоже нативно: серверный тег (STATIC_SEO_BY_PATH,
  // статьи) правится на месте, второй не появляется. robots={false} —
  // тег robots целиком за сервером (NOINDEX_EXACT), страница его не трогает.
  useMetaByName('description', description);
  useMetaByName(robots ? 'robots' : '', noindex || NOINDEX ? 'noindex, nofollow' : 'index, follow');

  return (
    <Helmet>
      {/* Basic Meta */}
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Language */}
      <html lang="ru" />
    </Helmet>
  );
}

// FAQ Schema component for JSON-LD
export function FAQSchema({ page }) {
  const faqs = FAQ_DATA[page];
  if (!faqs || faqs.length === 0) return null;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
